import React, { useState, useEffect, useRef, RefObject } from "react";

import "../../../style/create-new-app-env.module.css";
import "../../../style/license-key-modal.module.css";

import InlineNotification from "../../elements/Notification";
import CheckBox from "../../elements/checkbox";
import getPaidFeatures, { LicenseKeyFeaturesList } from "../../../api/st/features/get";
import { createLicenseKey } from "../../../api/st/license/user/post";
import { getUserInformation } from "../../../api/user/info";
import { usePaymentsModal } from "../../../context/PaymentsModalContext";
import { usePaymentDetailsContext } from "../../../utils/payments";
import { PaymentsError } from "../../../utils/customErrors";
import saasAnalytics from "../../../dashboard/saas/saasAnalytics";
import { getStripeRedirectURLs } from "../../../utils";
import { featureIDToPricingConfig, clientSideOnlyFeatureList } from "./config";

export interface LicenseKeyModalContentProps {
    onCloseModal: () => void;
}

export default function LicenseKeyModalContent({ onCloseModal }: LicenseKeyModalContentProps) {
    const [userEmail, setUserEmail] = useState("");
    const refs = useRef<RefObject<HTMLDivElement>[]>([]);
    const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

    const setRef = (el: HTMLDivElement | null, index: number) => {
        if (el) {
            refs.current[index] = { current: el };
        }
    };

    const paymentDetailsContext = usePaymentDetailsContext();

    let defaultSelectedFeatures: string[] = [];

    if (paymentDetailsContext !== undefined && paymentDetailsContext.savedContext.action_type === "license-key") {
        defaultSelectedFeatures = paymentDetailsContext.savedContext.data.selectedFeatures;
    }

    const [selectedFeatures, setSelectedFeatures] = useState<string[]>(defaultSelectedFeatures);

    const [features, setFeatures] = useState<LicenseKeyFeaturesList>([]);
    const [isFetchingFeatures, setFetchingFeatures] = useState(false);
    const [isFetchingFeaturesError, setFetchingFeaturesError] = useState(false);

    const [emailSent, setEmailSent] = useState(false);
    const [isSendingEmail, setSendingEmail] = useState(false);
    const [isSendingEmailError, setSendingEmailError] = useState(false);

    const { openModal: openPaymentsModal } = usePaymentsModal();

    async function getFeatures() {
        try {
            setFetchingFeatures(true);
            setFetchingFeaturesError(false);
            const response = await getPaidFeatures();
            setFeatures(response);
        } catch (_) {
            setFetchingFeaturesError(true);
        } finally {
            setFetchingFeatures(false);
        }
    }

    async function sendEmail() {
        try {
            setSendingEmailError(false);
            setSendingEmail(true);
            const { cancelURL, successURL } = getStripeRedirectURLs();
            await createLicenseKey({ featureIds: selectedFeatures, cancelURL, successURL });
            saasAnalytics.enablePaidFeaturesSendLicenseKeyToEmail(selectedFeatures);
            setEmailSent(true);
        } catch (e) {
            if (PaymentsError.isThisError(e)) {
                onCloseModal();
                saasAnalytics.paymentDetailsPopup("license_key");
                openPaymentsModal({
                    redirect_uri: e.redirectionURL,
                    action_type: "license-key",
                    data: {
                        selectedFeatures: selectedFeatures
                    },
                    timestamp: Date.now()
                });
            } else {
                setSendingEmailError(true);
            }
        } finally {
            setSendingEmail(false);
        }
    }

    function handleCheckboxChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (e.currentTarget.checked) {
            saasAnalytics.enablePaidFeaturesSelecteFeatureClick("self_hosted", e.currentTarget.id);
            setSelectedFeatures([...selectedFeatures, e.currentTarget.id]);
        } else {
            setSelectedFeatures(selectedFeatures.filter(featureId => featureId !== e.currentTarget.id));
        }
    }

    async function getUserEmail() {
        try {
            const user = await getUserInformation();
            setUserEmail(user.email);
        } catch (_) {}
    }

    useEffect(() => {
        getFeatures();
        getUserEmail();
    }, []);

    useEffect(() => {
        if (
            paymentDetailsContext !== undefined &&
            paymentDetailsContext.savedContext.action_type === "license-key" &&
            paymentDetailsContext.isPaymentSuccess === "true"
        ) {
            //if the payments details are successfully entered then continue with the license key generation action.
            sendEmail();
        }
    }, [paymentDetailsContext]);

    if (emailSent) {
        return (
            <div className="modal-container max-content">
                <div className="modal-header">
                    <h2>Email Sent!</h2>
                    <span
                        className="close-btn"
                        onClick={() => {
                            saasAnalytics.enablePaidFeaturesModalClose("success-self-hosted");
                            onCloseModal();
                        }}
                    >
                        <img src="static/assets/dashboard/close-rounded.png" />
                    </span>
                </div>
                <div className="modal-body">
                    <div className="success-container">
                        <div className="message-container">
                            <img src="/static/assets/dashboard/green-check.svg" alt="check" />
                            <div>
                                <p>License key has been sent to your email ID:</p>
                                <p className="email">{userEmail}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`modal-container generate-license-key-modal ${activeTooltip ? "modal-with-popover" : ""}`}>
            <div className="modal-header generate-license-key-modal-header">
                <h2>Generate license key</h2>
                <span
                    onClick={() => {
                        saasAnalytics.enablePaidFeaturesModalClose("generate-licensekey-for-self-hosted");
                        onCloseModal();
                    }}
                >
                    <img src="static/assets/dashboard/close-rounded.png" />
                </span>
            </div>
            <div className="modal-body">
                <div className="info-blue-container">
                    <span className="info-blue-pill">Info</span>
                    <p>
                        Some of the features (listed below) are{" "}
                        <a href="/pricing" target="_blank">
                            not in the open source version
                        </a>{" "}
                        of SuperTokens. To enable them, you need to generate a license key and add that to your
                        SuperTokens core instance.
                    </p>
                </div>
                <div className="select-feature-container">
                    <span className="select-feature-heading">Select required features</span>
                    <span className="select-price-heading">Price</span>
                </div>
                <div className="select-feature-body">
                    {isFetchingFeatures ? (
                        <div className="center">
                            <img className="loader" src="/static/assets/dashboard/loading.svg" alt="loading" />
                        </div>
                    ) : null}
                    {isFetchingFeaturesError ? (
                        <div className="center">
                            <div className="error-msg-container">
                                <img height={16} width={18} src="/static/assets/dashboard/alert.png" alt="alert" />
                                <p className="error-msg">Something went wrong. Please try again!</p>
                            </div>
                        </div>
                    ) : null}
                    {features.length ? (
                        <div className={`checkboxes-container ${isSendingEmail === true ? "pointer-events-none" : ""}`}>
                            {[...features, ...clientSideOnlyFeatureList].map((feature, index) => {
                                const featureConfig =
                                    featureIDToPricingConfig[
                                        feature.featureId as keyof typeof featureIDToPricingConfig
                                    ];

                                return (
                                    <div className="feature-item" key={feature.featureId}>
                                        <div key={feature.featureId} className="checkbox-container">
                                            <CheckBox
                                                key={feature.featureId}
                                                className="size-16"
                                                disabled={isSendingEmail || clientSideOnlyFeatureList.includes(feature)}
                                                id={feature.featureId}
                                                onChange={handleCheckboxChange}
                                                color="primary"
                                                checked={selectedFeatures.includes(feature.featureId)}
                                            />
                                            <label htmlFor={feature.featureId}>{feature.featureName}</label>
                                        </div>
                                        <div className="feature-price-detail">
                                            <div className="feature-price-container">
                                                {(() => {
                                                    switch (featureConfig.type) {
                                                        case "ATTACK_PROTECTION_SUITE":
                                                            return (
                                                                <a
                                                                    className="feature-price"
                                                                    href="/consultancy"
                                                                    target="_blank"
                                                                >
                                                                    {featureConfig.pricing.text}
                                                                </a>
                                                            );
                                                        default:
                                                            return (
                                                                <span className="feature-price">
                                                                    {featureConfig.pricing.text}
                                                                </span>
                                                            );
                                                    }
                                                })()}

                                                {featureConfig.hasDialog ? (
                                                    <div
                                                        className="info-icon-container"
                                                        ref={el => setRef(el, index)}
                                                        onMouseEnter={() => setActiveTooltip(feature.featureId)}
                                                        onMouseLeave={() => setActiveTooltip(null)}
                                                    >
                                                        <svg
                                                            width="12"
                                                            height="13"
                                                            viewBox="0 0 12 13"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <path
                                                                d="M5.6875 0.8125C4.56262 0.8125 3.463 1.14607 2.5277 1.77102C1.59239 2.39597 0.863411 3.28423 0.432938 4.32349C0.00246429 5.36274 -0.110167 6.50631 0.109286 7.60958C0.32874 8.71284 0.870422 9.72626 1.66583 10.5217C2.46124 11.3171 3.47466 11.8588 4.57793 12.0782C5.68119 12.2977 6.82476 12.185 7.86401 11.7546C8.90327 11.3241 9.79153 10.5951 10.4165 9.6598C11.0414 8.7245 11.375 7.62488 11.375 6.5C11.3734 4.99207 10.7737 3.54636 9.70741 2.48009C8.64114 1.41382 7.19543 0.814092 5.6875 0.8125ZM5.6875 11.3125C4.73568 11.3125 3.80523 11.0303 3.01382 10.5014C2.22241 9.97264 1.60558 9.22103 1.24133 8.34166C0.877085 7.46229 0.781782 6.49466 0.967473 5.56113C1.15316 4.62759 1.61151 3.77009 2.28455 3.09705C2.95759 2.42401 3.8151 1.96566 4.74863 1.77997C5.68216 1.59428 6.6498 1.68958 7.52917 2.05383C8.40854 2.41808 9.16015 3.03491 9.68895 3.82632C10.2178 4.61773 10.5 5.54818 10.5 6.5C10.4986 7.77591 9.99106 8.99915 9.08885 9.90135C8.18665 10.8036 6.96341 11.3111 5.6875 11.3125ZM6.5625 9.125C6.5625 9.24103 6.51641 9.35231 6.43436 9.43436C6.35231 9.51641 6.24103 9.5625 6.125 9.5625C5.89294 9.5625 5.67038 9.47031 5.50628 9.30622C5.34219 9.14212 5.25 8.91956 5.25 8.6875V6.5C5.13397 6.5 5.02269 6.45391 4.94064 6.37186C4.8586 6.28981 4.8125 6.17853 4.8125 6.0625C4.8125 5.94647 4.8586 5.83519 4.94064 5.75314C5.02269 5.67109 5.13397 5.625 5.25 5.625C5.48207 5.625 5.70463 5.71719 5.86872 5.88128C6.03281 6.04538 6.125 6.26794 6.125 6.5V8.6875C6.24103 8.6875 6.35231 8.73359 6.43436 8.81564C6.51641 8.89769 6.5625 9.00897 6.5625 9.125ZM4.8125 4.09375C4.8125 3.96396 4.85099 3.83708 4.9231 3.72916C4.99521 3.62124 5.0977 3.53712 5.21762 3.48745C5.33753 3.43778 5.46948 3.42479 5.59678 3.45011C5.72408 3.47543 5.84101 3.53793 5.93279 3.62971C6.02457 3.72149 6.08707 3.83842 6.11239 3.96572C6.13771 4.09302 6.12472 4.22497 6.07505 4.34489C6.02538 4.4648 5.94126 4.56729 5.83334 4.6394C5.72543 4.71151 5.59855 4.75 5.46875 4.75C5.2947 4.75 5.12778 4.68086 5.00471 4.55779C4.88164 4.43472 4.8125 4.2678 4.8125 4.09375Z"
                                                                fill="#007AFF"
                                                            />
                                                        </svg>
                                                        {activeTooltip === feature.featureId &&
                                                            featureConfig.hasDialog && (
                                                                <div className="feature-price-tooltip">
                                                                    {(() => {
                                                                        switch (featureConfig.type) {
                                                                            case "ACCOUNT_LINKING":
                                                                                const AccountLinkingDialog =
                                                                                    featureConfig.dialog;
                                                                                const AccountLinkingConfig =
                                                                                    featureConfig.dialogConfig;
                                                                                return (
                                                                                    <AccountLinkingDialog
                                                                                        config={AccountLinkingConfig}
                                                                                    />
                                                                                );
                                                                            case "DASHBOARD_LOGIN":
                                                                                const DashboardLoginDialog =
                                                                                    featureConfig.dialog;
                                                                                const DashboardLoginConfig =
                                                                                    featureConfig.dialogConfig;
                                                                                return (
                                                                                    <DashboardLoginDialog
                                                                                        config={DashboardLoginConfig}
                                                                                    />
                                                                                );

                                                                            case "MULTI_TENANCY":
                                                                                const MultiTenancyDialog =
                                                                                    featureConfig.dialog;
                                                                                const MultiTenancyConfig =
                                                                                    featureConfig.dialogConfig;
                                                                                return (
                                                                                    <MultiTenancyDialog
                                                                                        config={MultiTenancyConfig}
                                                                                    />
                                                                                );
                                                                        }
                                                                    })()}
                                                                </div>
                                                            )}
                                                    </div>
                                                ) : null}
                                            </div>
                                            <div className="feature-sub-text">
                                                {"minimumBilling" in featureConfig.pricing ? (
                                                    <span>
                                                        Minimum Billing of $
                                                        <span className="feature-minimum-billing">
                                                            {featureConfig.pricing.minimumBilling} /month
                                                        </span>
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : null}
                </div>
                {isSendingEmailError ? (
                    <div className="notification-holder margin-bottom-28 width-fit-content">
                        <InlineNotification status="error">Something went wrong. Please try again!</InlineNotification>
                    </div>
                ) : null}
            </div>
            <div className="modal-action-container margin-top-20">
                {isFetchingFeaturesError ? (
                    <button onClick={getFeatures} className="btn btn-sm white-button">
                        Retry
                    </button>
                ) : null}
                <button
                    onClick={sendEmail}
                    disabled={selectedFeatures.length === 0 || isSendingEmail}
                    className={`${isSendingEmail ? "yellow-button-loading" : ""} ${
                        selectedFeatures.length === 0 ? "disabled-butn" : ""
                    } btn btn-sm yellow-button`}
                >
                    {isSendingEmail ? (
                        <img
                            height={20}
                            className="loader"
                            src="/static/assets/dashboard/white-loader.svg"
                            alt="loading"
                        />
                    ) : null}
                    Send license key to email
                </button>
            </div>
        </div>
    );
}
